import { Injectable } from '@angular/core';

import { Storage } from '@ionic/storage-angular';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private thisStorage: Storage | null = null;

  /**
   * Cached promise from the very first `init()` call. We hold onto it so
   * concurrent callers (and the APP_INITIALIZER hook in `app.module.ts`)
   * all wait on the same `Storage.create()` rather than racing — the
   * previous version called `init()` from the constructor *and* fell
   * back to `init()` on every storage op when `isInitialized` stayed
   * false, which kept re-creating the underlying DB on cold-start
   * failures and could lose tokens to the race.
   */
  private initPromise: Promise<void> | null = null;

  // The constructor used to fire-and-forget `this.init()` here, swallow
  // any error, and leave `isInitialized = false` so every subsequent
  // storage call would call `init()` again. The bootstrap wiring in
  // `AppModule` now calls `init()` via APP_INITIALIZER and awaits it
  // before the first guarded route mounts, so the constructor stays
  // side-effect free.
  constructor(private storage: Storage) {}

  public init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = (async () => {
      this.thisStorage = await this.storage.create();
    })().catch((error) => {
      // Drop the cached promise so a manual retry (e.g. the user
      // re-logging in) can attempt init again instead of resolving
      // immediately with a still-null `thisStorage`.
      this.initPromise = null;
      console.error('Storage initialisatie fout:', error);
      throw error;
    });
    return this.initPromise;
  }

  public async getItem(key: string): Promise<any> {
    await this.ensureInitialized();
    if (!this.thisStorage) {
      console.error('Storage is nog niet klaar.');
      return null;
    }
    try {
      return JSON.parse(await this.thisStorage.get(key));
    } catch (error) {
      console.error('Fout bij ophalen van item:', error);
      throw error;
    }
  }

  public removeItem(key: string): void {
    this.thisStorage?.remove(key);
  }

  // Methoden voor gebruik met localStorage
  public saveLocalData(key: string, value: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Fout bij localStorage set:', error);
    }
  }

  // Create and expose methods that users of this service can
  // call, for example:
  public set(key: string, value: any) {
    this.thisStorage?.set(key, value);
  }

  public getLocalData(key: string): any {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Fout bij ophalen uit localStorage', error);
      return null;
    }
  }

  public async setItem(key: string, value: any): Promise<void> {
    await this.ensureInitialized();

    if (!this.isStorageReady()) {
      return;
    }

    try {
      const jsonString = JSON.stringify(value);
      await this.thisStorage.set(key, jsonString);
    } catch (error) {
      console.error('Fout bij opslaan van item:', error);
    }
  }

  public async clearLocalData(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
      await this.thisStorage.remove(key);
    } catch (error) {
      console.error('Fout bij verwijderen uit localStorage', error);
    }
  }

  private async ensureInitialized(): Promise<void> {
    await this.init();
  }

  private isStorageReady(): boolean {
    if (!this.thisStorage) {
      console.error('Storage is niet geïnitialiseerd. Roep init() aan.');
      return false;
    }
    return true;
  }

}
